import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardHeader } from '../../../../components/ui/Card';
import { useDomainReport } from '../../../../hooks/useVisibility';
import { useFilters } from '../filters';
import { CHART_SEMANTIC, CHART_TOOLTIP, RAMP_INK, rampColor } from '../../../../components/ui/chart';
import { EmptyState, LoadingState } from '../../../../components/ui/States';

const CLASS_LABELS: Record<string, string> = {
  corporate: 'Corporate',
  ugc: 'UGC',
  reference: 'Reference',
  competitor: 'Competitor',
  unknown: 'Unknown',
};

export function DomainTypeDonut() {
  const { startDate, endDate } = useFilters();
  const { data, isLoading } = useDomainReport(startDate, endDate);

  const { slices, total } = useMemo(() => {
    if (!data) return { slices: [] as { key: string; label: string; value: number; color: string }[], total: 0 };
    const classIdx = data.columns.indexOf('classification');
    const retrievedIdx = data.columns.indexOf('retrieval_count');
    const tally = new Map<string, number>();
    let sum = 0;
    for (const row of data.rows) {
      const cls = ((row[classIdx] as string | null) ?? 'unknown').toLowerCase();
      const n = (row[retrievedIdx] as number | null) ?? 0;
      tally.set(cls, (tally.get(cls) ?? 0) + n);
      sum += n;
    }
    // Slices are parts of one total, so they take a single-hue ramp ordered largest-first
    // rather than a categorical rainbow. "Unknown" stays grey — it is absence, not a category.
    const out = Array.from(tally.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([key, value], i) => ({
        key,
        label: CLASS_LABELS[key] ?? key,
        value,
        color: key === 'unknown' ? CHART_SEMANTIC.neutral : rampColor(RAMP_INK, i),
      }));
    return { slices: out, total: sum };
  }, [data]);

  return (
    <Card padding="md">
      <CardHeader title="Domain types" description={`${total.toLocaleString()} total retrievals`} />
      {isLoading ? (
        <LoadingState />
      ) : total === 0 ? (
        <EmptyState compact title="No domains retrieved yet." />
      ) : (
        <div className="grid grid-cols-2 gap-4 items-center">
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={1}
                  stroke="#ffffff"
                  strokeWidth={2} isAnimationActive={false}
                >
                  {slices.map((s) => <Cell key={s.key} fill={s.color} />)}
                </Pie>
                <Tooltip {...CHART_TOOLTIP} formatter={(v) => [(v as number).toLocaleString(), 'retrievals']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="space-y-1.5 text-body-sm">
            {slices.map((s) => {
              const p = total > 0 ? (s.value / total) * 100 : 0;
              return (
                <li key={s.key} className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-text-secondary">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                    {s.label}
                  </span>
                  <span className="tabular-nums text-text-tertiary">{p.toFixed(1)}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}
